import { useEffect, useMemo, useState } from 'react';

import { AxiosResponse } from 'axios';
import { useParams } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';

import { useMeetingRooms } from './hooks/useMeetingRooms';
import RoomAgenda from './roomAgenda';
import RoomEventActions from './roomEventActions';
import RoomEventParticipants from './roomEventParticipants';
import RoomVerticalRail from './RoomVerticalRail';
import * as S from './styles';
import { EventParticipant } from './types/eventTypes';
import { IRoomAgenda, IRoomState, RoomAgendaEvent, OccurrenceStatus } from './types/roomAgenda';
import Spin from 'components/Spin';
import { JitsiVideoEmbed } from 'components/videoEmbeders/JitsiVideoEmbed';
import { YoutubeVideoEmbed } from 'components/videoEmbeders/YoutubeVideoEmbed';
import { useMeeting } from 'global/MeetingContext';
import { useUser } from 'global/UserContext';
import { apiMeeting, apiMain } from 'services/apiService';
import { MenuItem, isMeetingRoomExtras } from 'types/menuTypes';
import { useIsMobile } from 'utils/mediaQueries';

interface RoomsProps {
  documentId?: string;
  collections?: any[];
  index?: number;
  roomId?: string;
  roomName?: string;
  vendorCode?: string;
  roomKey?: string;
  background?: any;
  menu?: MenuItem;
}

const Rooms: React.FC<RoomsProps> = ({
  documentId: propDocumentId,
  collections: propCollections,
  index,
  roomId: propRoomId,
  roomName: propRoomName,
  vendorCode: propVendorCode,
  roomKey: propRoomKey,
  background: propBackground,
  menu,
}) => {
  const isMobile = useIsMobile();
  const { user } = useUser();
  const [roomAgenda, setRoomAgenda] = useState<IRoomAgenda | null>(null);
  const [roomState, setRoomState] = useState<IRoomState | null>(null);

  // Dados do evento
  const [selectedEvent, setSelectedEvent] = useState<RoomAgendaEvent | null>(null);
  const [selectedOccurrenceKey, setSelectedOccurrenceKey] = useState<string | null>(null);
  const [eventParticipants, setEventParticipants] = useState<EventParticipant[]>([]);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [selectedOccurrenceStatus, setSelectedOccurrenceStatus] =
    useState<OccurrenceStatus>('UPCOMING');

  // Se collections não for passado, busca via hook
  const { collections: hookCollections, loading, error } = useMeetingRooms();
  const collections = propCollections || hookCollections;

  // documentId pode vir por prop ou pela URL
  const { roomId: urlRoomId } = useParams();
  const documentId = propDocumentId || urlRoomId;

  // Estado para controlar o loading do vídeo
  const [videoLoading, setVideoLoading] = useState(true);

  // Estado para controlar o ID de requisição de entrada
  const { activeRoom, isActive } = useMeeting();
  const [enterRequestId, setEnterRequestId] = useState<number>(0);

  // Busca a sala pelo documentId ou internal_id
  const room = useMemo(() => {
    // Se temos um menu com extras de sala de reunião, usamos esses dados
    if (menu?.extras && isMeetingRoomExtras(menu.extras)) {
      const organization_uuid = user?.user_to_organization[0]?.organization.uuid;
      return {
        id: menu.feature?.id,
        room_data: {
          title: menu.label,
          vendor_code: menu.extras.vendor_code,
          key: menu.extras.key,
          internal_id: menu.extras.internal_id,
          link: menu.extras.link,
          background: menu.extras.background,
          organization_uuid,
          event_id: selectedEvent?.id,
        },
        participation_status: {
          is_participant: true,
        },
      };
    }

    // Caso contrário, busca nas collections
    if (!documentId) return null;

    for (const collection of collections) {
      for (const room of collection.rooms) {
        if (
          room.id?.toString() === documentId ||
          room.feature?.documentId === documentId ||
          room.internal_id === documentId
        ) {
          return room;
        }
      }
    }
    return null;
  }, [collections, documentId, menu]);

  // Se temos props diretas, elas têm prioridade
  const finalRoom = useMemo(() => {
    if (!room) return null;

    return {
      ...room,
      id: room.id || propRoomId,
      room_data: {
        ...room.room_data,
        title: room.room_data?.title || propRoomName || room.label,
        vendor_code: room.room_data?.vendor_code || propVendorCode,
        key: room.room_data?.key || propRoomKey,
        background: room.room_data?.background || propBackground,
        organization_uuid: room.organization_uuid ?? room.room_data?.organization_uuid,
        event_id: selectedEvent?.id ?? room.room_data?.event_id,
      },
      participation_status: {
        is_participant: true,
      },
    };
  }, [room, propRoomId, propRoomName, propVendorCode, propRoomKey, propBackground]);

  useEffect(() => {
    const fetchRoomAgenda = async () => {
      const today = new Date().toISOString().split('T')[0];
      const response: AxiosResponse<IRoomAgenda> = await apiMeeting.get(
        `/v1/room-schedule/agenda/${finalRoom.room_data.internal_id}?init_date=${today}`
      );
      setRoomAgenda(response.data);
    };

    const fetchRoomState = async () => {
      const eventId = selectedEvent?.id;
      const response: AxiosResponse<any> = await apiMeeting.get(
        `/v1/rooms/state/${finalRoom.room_data.internal_id}?is_open=true` +
          (eventId ? `&event_id=${eventId}` : '')
      );
      setRoomState(response.data);
    };

    fetchRoomAgenda();
    fetchRoomState();
  }, [finalRoom]);

  // Auto-select "Acontecendo" when agenda arrives
  useEffect(() => {
    if (!roomAgenda) return;
    const now = new Date();
    const nowIso = now.toISOString();
    for (const schedule of roomAgenda.schedules || []) {
      const startIso = `${schedule.init_date}T${schedule.init_time}Z`;
      const endIso = `${schedule.end_date}T${schedule.end_time}Z`;
      if (nowIso >= startIso && nowIso <= endIso) {
        const ev = roomAgenda.events.find(e => e.id === schedule.event_id);
        if (ev) {
          setSelectedEvent(ev);
          setSelectedOccurrenceKey(`${ev.id}-${startIso}`);
          // setIsPanelOpen(true); // auto-open details when ongoing
          break;
        }
      }
    }
  }, [roomAgenda]);

  // Busca os participantes do evento selecionado
  useEffect(() => {
    if (!selectedEvent) return;
    const fetchEventParticipants = async () => {
      const response: AxiosResponse<EventParticipant[]> = await apiMain.get(
        `events/v1/participants/${selectedEvent.id}`
      );
      setEventParticipants(response.data);
    };
    fetchEventParticipants();
  }, [selectedEvent]);

  // Função para entrar na reunião
  const handleEnterMeeting = () => {
    setEnterRequestId(enterRequestId + 1);
  };

  if (loading) return <Spin />;
  if (error) return <Typography color="error">Erro ao carregar salas: {error}</Typography>;
  if (!finalRoom) return <Typography>Sala não encontrada.</Typography>;

  // Se for YouTube, renderiza o YoutubeVideoEmbed
  if (finalRoom.room_data?.vendor_code === 'YOUTUBE') {
    return (
      <YoutubeVideoEmbed
        videoId={finalRoom.room_data?.link}
        videoLoading={videoLoading}
        onLoad={() => setVideoLoading(false)}
      />
    );
  }

  const shouldRenderRoomData =
    !isActive || activeRoom?.room_data?.internal_id !== finalRoom.room_data?.internal_id;

  return (
    <>
      <Box display={shouldRenderRoomData ? 'none' : 'flex'} height="100%" width="100%">
        <JitsiVideoEmbed
          meetingRoom={finalRoom}
          videoLoading={videoLoading}
          onLoad={() => setVideoLoading(false)}
          collectionIndex={index}
          enterRequestId={enterRequestId}
        />
      </Box>
      <S.Wrapper
        backgroundImage={finalRoom.room_data?.background?.url}
        isMobile={isMobile}
        isActive={isActive}
      >
        {shouldRenderRoomData && (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            width="100%"
            justifyContent="space-between"
          >
            <Stack direction={{ xs: 'column', md: 'row' }} width="100%">
              {shouldRenderRoomData && (
                <RoomVerticalRail
                  onToggle={() => setIsPanelOpen(prev => !prev)}
                  onEnter={handleEnterMeeting}
                />
              )}
              {shouldRenderRoomData && isPanelOpen && (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent={{ xs: 'flex-start', md: 'space-between' }}
                  spacing={2}
                  width="100%"
                  height="100%"
                >
                  {roomAgenda && (
                    <RoomAgenda
                      roomAgenda={roomAgenda}
                      onSelectOccurrence={({ event, occurrenceKey, status }) => {
                        setSelectedEvent(event);
                        setSelectedOccurrenceKey(occurrenceKey);
                        setSelectedOccurrenceStatus(status);
                      }}
                      selectedOccurrenceKey={selectedOccurrenceKey}
                    />
                  )}

                  {/* {roomAgenda && selectedEvent && (
                  <RoomEventSummary event={selectedEvent} participants={eventParticipants} />
                )} */}

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 16,
                      height: '100%',
                      marginRight: '.5rem',
                    }}
                  >
                    {roomAgenda && selectedEvent && (
                      <RoomEventParticipants
                        event={selectedEvent}
                        participants={eventParticipants}
                        roomState={roomState}
                      />
                    )}
                  </div>
                </Stack>
              )}
            </Stack>

            <RoomEventActions
              event={selectedEvent}
              onEnterMeeting={handleEnterMeeting}
              roomState={roomState}
              status={selectedOccurrenceStatus}
            />
          </Stack>
        )}
      </S.Wrapper>
    </>
  );
};

export default Rooms;
